import React, { useState, useEffect, useRef } from 'react';

interface CircularProgressProps {
  progress: number; // 0-100
  strokeWidth?: number;
  label?: string;
  value?: string | number;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  strokeWidth = 8,
  label,
  value
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState('1s');
  const isFirstMount = useRef(true);

  // Use viewBox for responsive SVG - this ensures perfect circles
  const viewBoxSize = 100;
  const center = viewBoxSize / 2;
  const radius = (viewBoxSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (displayProgress / 100) * circumference;

  useEffect(() => {
    // On first mount, start at 0 and immediately transition to target
    if (isFirstMount.current) {
      isFirstMount.current = false;
      setTransitionDuration('1s');
      // Use double RAF to ensure CSS transition applies immediately
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDisplayProgress(progress);
        });
      });
    } else {
      // On subsequent updates, use shorter transition
      setTransitionDuration('0.5s');
      setDisplayProgress(progress);
    }
  }, [progress]);

  return (
    <div className="relative w-full aspect-square flex items-center justify-center bg-primary rounded-full p-2 max-w-full">
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="w-full h-full transform -rotate-90"
        style={{ filter: 'drop-shadow(0 0 8px rgba(0, 255, 136, 0.3))' }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" className="text-emerald-400" style={{ stopColor: 'currentColor' }} />
            <stop offset="50%" className="text-white" style={{ stopColor: 'currentColor' }} />
            <stop offset="100%" className="text-gray-900" style={{ stopColor: 'currentColor' }} />
          </linearGradient>
        </defs>

        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-700 opacity-30"
        />

        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: `stroke-dashoffset ${transitionDuration} cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-2">
        {value && (
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-accent leading-none transition-opacity duration-500">
            {value}
          </div>
        )}
        {label && (
          <div className="text-xs text-text-secondary mt-1 leading-tight">
            {label}
          </div>
        )}
      </div>
    </div>
  );
}; 